import React, { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import {
  LuCalendarClock, LuFileText, LuHistory, LuImage, LuLayoutDashboard, LuLinkedin, LuLogOut, LuMenu,
  LuMessageSquare, LuPlus, LuSearchCheck, LuTag, LuUsers, LuX,
} from "react-icons/lu";
import { getAdminSession, adminLogout } from "../controller/api";
import PageLoader from "./PageLoader";

const NAV_ITEMS = [
  { to: "/admin", label: "Overview", Icon: LuLayoutDashboard, end: true },
  { to: "/admin/posts", label: "Stories", Icon: LuFileText },
  { to: "/admin/categories", label: "Topics", Icon: LuTag },
  { to: "/admin/comments", label: "Comments", Icon: LuMessageSquare },
  // { to: "/admin/media", label: "Media library", Icon: LuImage },
];

const PUBLISHING_TOOLS_NAV_ITEMS = [
  { to: "/admin/calendar", label: "Content calendar", Icon: LuCalendarClock },
  { to: "/admin/seo", label: "SEO center", Icon: LuSearchCheck },
  { to: "/admin/audit-log", label: "Audit log", Icon: LuHistory },
  { to: "/admin/linkedin-queue", label: "LinkedIn queue", Icon: LuLinkedin },
];

const ADMIN_ONLY_NAV_ITEMS = [
  { to: "/admin/users", label: "Users", Icon: LuUsers },
];

const pageName = (pathname, navItems) => {
  if (pathname.includes("/posts/new")) return "New story";
  if (pathname.includes("/posts/") && pathname.includes("/edit")) return "Edit story";
  if (pathname.includes("/posts/") && pathname.includes("/stats")) return "Story stats";
  return navItems.find((item) => item.end ? pathname === item.to : pathname.startsWith(item.to))?.label || "Studio";
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [session, setSession] = useState(null); // { email, role }

  useEffect(() => {
    getAdminSession().then(setSession).catch(() => {});
  }, []);

  const roleItems = session?.role === "admin" ? ADMIN_ONLY_NAV_ITEMS : [];
  const navItems = [...NAV_ITEMS, ...PUBLISHING_TOOLS_NAV_ITEMS, ...roleItems];

  const handleLogout = async () => {
    await adminLogout();
    navigate("/admin/login");
  };

  const sidebar = (
    <>
      <div className="px-5 py-6 border-b border-[#284438]">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--leaf)] text-[var(--pine)] font-black" style={{ fontFamily: "Syne, sans-serif" }}>i</span>
          <div>
            <p className="text-sm font-bold tracking-tight text-white" style={{ fontFamily: "Syne, sans-serif" }}>InQmise</p>
            <p className="text-[10px] font-medium uppercase tracking-[.16em] text-[#9eb9a4]">Publishing studio</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-5 overflow-y-auto" aria-label="Admin navigation">
        {[
          { heading: "Workspace", items: NAV_ITEMS },
          { heading: "Publishing tools", items: PUBLISHING_TOOLS_NAV_ITEMS },
          ...(roleItems.length ? [{ heading: "Admin", items: roleItems }] : []),
        ].map(({ heading, items }) => (
          <div key={heading} className="space-y-1">
            <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[.16em] text-[#78927e]">{heading}</p>
            {items.map(({ to, label, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[#244936] text-white shadow-[inset_3px_0_0_var(--leaf)]" : "text-[#bbcfbf] hover:bg-[#1c422f] hover:text-white"
                }`}
              >
                {React.createElement(Icon, { className: "text-base", "aria-hidden": true })}
                <span className="flex-1">{label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-[#284438] p-3">
        {session && (
          <p className="px-3 pb-2 text-xs text-[#9eb9a4] truncate" title={session.email}>
            {session.email} · {session.role === "admin" ? "Admin" : "Editor"}
          </p>
        )}
        <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#bbcfbf] transition-colors hover:bg-[#402c2c] hover:text-white">
          <LuLogOut aria-hidden="true" /> Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f4f6f1] text-[var(--ink)]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-[var(--pine)] lg:flex">{sidebar}</aside>
      {menuOpen && <button aria-label="Close navigation" onClick={() => setMenuOpen(false)} className="fixed inset-0 z-40 bg-[#10281c]/45 lg:hidden" />}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-[var(--pine)] shadow-2xl transition-transform lg:hidden ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <button aria-label="Close navigation" onClick={() => setMenuOpen(false)} className="absolute right-3 top-3 rounded-lg p-2 text-[#bbcfbf] hover:bg-[#1c422f]"><LuX /></button>
        {sidebar}
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-[70px] items-center justify-between border-b border-[#dce5da] bg-[#f9fbf7]/95 px-5 backdrop-blur sm:px-8">
          <div className="flex items-center gap-3">
            <button aria-label="Open navigation" onClick={() => setMenuOpen(true)} className="rounded-lg p-2 text-[var(--ink)] hover:bg-[var(--mist)] lg:hidden"><LuMenu /></button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[.16em] text-[var(--muted)]">Content desk</p>
              <h1 className="text-base font-bold tracking-tight" style={{ fontFamily: "Syne, sans-serif" }}>{pageName(location.pathname, navItems)}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/admin/posts/new")} className="inline-flex items-center gap-2 rounded-lg bg-[var(--pine)] px-3.5 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#174a34] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--leaf)]"><LuPlus className="text-base" /> <span className="hidden sm:inline">Write story</span></button>
          </div>
        </header>
        <main><Suspense fallback={<PageLoader />}><Outlet /></Suspense></main>
      </div>
    </div>
  );
};

export default AdminLayout;
